<script setup lang="ts">
import type { Component } from "vue";

import CloseIcon from "~/assets/icons/close-2.svg?component";
import CheckIcon from "~/assets/icons/check.svg?component";
import InfoIcon from "~/assets/icons/info-2.svg?component";
import WarningIcon from "~/assets/icons/warning.svg?component";
import type { Toast } from "~/composables/useToast";

const props = defineProps<{ toast: Toast }>();

defineEmits<{
  (e: "close", id: number): void;
}>();

const iconMap: Record<Toast["type"], Component> = {
  success: CheckIcon,
  error: CloseIcon,
  info: InfoIcon,
  warning: WarningIcon,
};

const icon = computed(() => iconMap[props.toast.type]);
</script>

<template>
  <div
    class="toast w-full rounded-xl border px-4 py-3.5 shadow-[0_10px_30px_rgba(15,23,42,0.10)]"
    :data-type="toast.type"
  >
    <div class="flex items-start gap-3">
      <!-- Icon -->
      <div
        class="icon flex size-8 shrink-0 items-center justify-center rounded-lg"
        :data-type="toast.type"
      >
        <component :is="icon" class="size-4" />
      </div>

      <!-- Content -->
      <div class="min-w-0 flex-1 pt-px">
        <div class="flex items-start gap-2">
          <div
            v-if="toast.title"
            class="min-w-0 flex-1 text-base font-semibold text-app-text"
          >
            {{ toast.title }}
          </div>

          <span
            v-if="toast.count > 1"
            class="count mt-px shrink-0 rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none"
            :data-type="toast.type"
          >
            x{{ toast.count }}
          </span>
        </div>

        <div class="mt-0.5 text-sm leading-5 text-app-muted">
          {{ toast.description }}
        </div>
      </div>

      <!-- Close button -->
      <button
        type="button"
        class="-mr-1 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-md text-[#A1A8B3] transition hover:bg-black/5 hover:text-app-text"
        @click="$emit('close', toast.id)"
      >
        <CloseIcon class="size-4" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.toast {
  border-color: #edf0f3;
}

.toast[data-type="success"] {
  background: color-mix(in srgb, theme("colors.primary.100") 62%, white);
  border-color: color-mix(
    in srgb,
    theme("colors.primary.DEFAULT") 24%,
    #edf0f3
  );
}

.toast[data-type="info"] {
  background: color-mix(in srgb, theme("colors.primary.100") 52%, white);
  border-color: color-mix(
    in srgb,
    theme("colors.primary.DEFAULT") 20%,
    #edf0f3
  );
}

.toast[data-type="warning"] {
  background: #fff8ea;
  border-color: #f6d696;
}

.toast[data-type="error"] {
  background: #fff1f1;
  border-color: #f2b8b8;
}

/* icon color */
.icon[data-type="success"],
.icon[data-type="info"] {
  background: white;
  color: theme("colors.primary.DEFAULT");
  box-shadow: inset 0 0 0 1px
    color-mix(in srgb, theme("colors.primary.DEFAULT") 18%, transparent);
}

.icon[data-type="warning"] {
  background: white;
  color: #c77700;
  box-shadow: inset 0 0 0 1px rgba(199, 119, 0, 0.18);
}

.icon[data-type="error"] {
  background: white;
  color: #d04444;
  box-shadow: inset 0 0 0 1px rgba(208, 68, 68, 0.18);
}

.count[data-type="success"],
.count[data-type="info"] {
  background: white;
  color: theme("colors.primary.DEFAULT");
}

.count[data-type="warning"] {
  background: white;
  color: #c77700;
}

.count[data-type="error"] {
  background: white;
  color: #d04444;
}
</style>
